import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  TextField,
  Stack,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { fetchMemberDetail } from '../../store/slices/membersSlice';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import MemberSocialWidget from '../../components/Members/MemberSocialWidget';
import SponsorNetworkGraph from '../../components/Members/SponsorNetworkGraph';
import SimilarMembersList from '../../components/Members/SimilarMembersList';
import MemberSummary from '../../components/Members/MemberSummary';
import FinanceTab from '../../components/Members/FinanceTab';

const MemberDetail: React.FC = () => {
  const { bioguideId } = useParams<{ bioguideId: string }>();

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentMember, loading, error } = useSelector((state: RootState) => state.members);
  const { token } = useAuth();
  const { authenticated, login } = useAuth();
  const [isFollowing, setIsFollowing] = useState<boolean | null>(null);

  // SEO: set page title and description when member loads
  useEffect(() => {
    const name = (currentMember && ((currentMember as any).fullName || (currentMember as any).name || `${(currentMember as any).firstName || ''} ${(currentMember as any).lastName || ''}`.trim())) || 'Member';
    const title = `${name} – Member Profile | AskTheBill.ai`;
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    const descParty = (currentMember as any)?.party ? `, ${(currentMember as any).party}` : '';
    const descState = (currentMember as any)?.state ? `, ${(currentMember as any).state}` : '';
    const description = `${name}${descParty}${descState} — sponsored bills, cosponsorships, service history, and AI-generated legislative focus.`;
    if (meta) {
      meta.setAttribute('content', description);
    } else {
      const m = document.createElement('meta');
      (m as any).name = 'description';
      (m as any).content = description;
      document.head.appendChild(m);
    }

    // Inject schema.org Person JSON-LD
    try {
      const scriptId = 'schema-person-jsonld';
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      const person = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name,
        image: (currentMember as any)?.imageUrl || (currentMember as any)?.image_url || undefined,
        affiliation: (currentMember as any)?.party || undefined,
        memberOf: (currentMember as any)?.chamber || undefined,
        address: (currentMember as any)?.state || undefined,
        sameAs: (currentMember as any)?.official_url || (currentMember as any)?.website || undefined,
      } as any;
      script.text = JSON.stringify(person);
      document.head.appendChild(script);
    } catch {}
  }, [currentMember]);

  const [sponsored, setSponsored] = useState<any[] | null>(null);
  const [cosponsored, setCosponsored] = useState<any[] | null>(null);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiSummaryStats, setAiSummaryStats] = useState<any>({});
  const [aiSummaryTime, setAiSummaryTime] = useState<string | null>(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState<boolean>(false);
  const [aiSummaryError, setAiSummaryError] = useState<string | null>(null);
  const [memberVotes, setMemberVotes] = useState<any[] | null>(null);
  // Local relevance: user location for badges
  const [myLocation, setMyLocation] = useState<any | null>(null);
  // Notes (member)
  const [noteText, setNoteText] = useState<string>('');
  const [noteSaved, setNoteSaved] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [miniQuestion, setMiniQuestion] = useState<string>('');

  // Load user location (for badges)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiService.getMyLocation();
        if (cancelled) return;
        setMyLocation(resp?.data?.location || null);
      } catch {
        setMyLocation(null);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticated]);

  // Load recent votes
  useEffect(() => {
    if (!bioguideId) { setMemberVotes(null); return; }
    (async () => {
      try {
        const resp = await apiService.getMemberVotes(bioguideId as string, { limit: 10, offset: 0 });
        setMemberVotes(Array.isArray(resp.data?.items) ? resp.data.items : []);
      } catch (e) {
        setMemberVotes([]);
      }
    })();
  }, [bioguideId]);

  // Compute total time served across all terms (years and months)
  const totalService = useMemo(() => {
    try {
      const terms: any[] = (currentMember && Array.isArray((currentMember as any).terms)) ? (currentMember as any).terms : [];
      if (!terms || terms.length === 0) return null;

      const today = new Date();

      // helper: parse start/end dates from a term, supporting multiple field shapes
      const parseTermRange = (term: any): { start: Date | null; end: Date | null } => {
        const getDate = (v: any, isStart: boolean): Date | null => {
          if (v === undefined || v === null) return null;
          try {
            if (typeof v === 'string') {
              // try ISO string first
              const d = new Date(v);
              if (!isNaN(d.getTime())) return d;
              // try YYYY or YYYY-MM
              const m = v.match(/^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/);
              if (m) {
                const year = parseInt(m[1], 10);
                const month = m[2] ? parseInt(m[2], 10) - 1 : (isStart ? 0 : 11);
                const day = m[3] ? parseInt(m[3], 10) : (isStart ? 1 : 28); // use 28 to avoid month overflow
                return new Date(year, month, day);
              }
            }
            if (typeof v === 'number') {
              // interpret as year
              const year = v;
              return new Date(year, isStart ? 0 : 11, isStart ? 1 : 31);
            }
          } catch {}
          return null;
        };

        // common field candidates
        const startCand = term.startDate || term.start || term.start_date || term.startYear || term.start_year;
        const endCand = term.endDate || term.end || term.end_date || term.endYear || term.end_year;

        const start = getDate(startCand, true);
        let end = getDate(endCand, false);
        if (!end || end > today) end = new Date(today);
        return { start, end };
      };

      // months difference between two dates (approx, counts partial months when day-of-month passes)
      const diffInMonths = (a: Date, b: Date): number => {
        let months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
        // adjust based on day-of-month
        if (b.getDate() < a.getDate()) months -= 1;
        return Math.max(0, months);
      };

      // Build normalized, merged non-overlapping service intervals to avoid double-counting
      const ranges: { start: Date; end: Date }[] = [];
      for (const t of terms) {
        const { start, end } = parseTermRange(t);
        if (start && end && end >= start) {
          ranges.push({ start, end });
        }
      }
      if (ranges.length === 0) return null;
      // sort by start asc
      ranges.sort((a, b) => a.start.getTime() - b.start.getTime());

      // Clamp each term's end to not exceed the start of the next term.
      // This prevents year-only end dates (e.g., 1993) from stretching to end-of-year
      // and overlapping heavily with the next term.
      for (let i = 0; i < ranges.length - 1; i++) {
        const cur = ranges[i];
        const nxt = ranges[i + 1];
        if (cur.end.getTime() > nxt.start.getTime()) {
          cur.end = new Date(nxt.start);
        }
      }

      // merge overlaps and contiguous intervals (where next.start <= prev.end)
      const merged: { start: Date; end: Date }[] = [];
      for (const r of ranges) {
        if (merged.length === 0) {
          merged.push({ ...r });
        } else {
          const last = merged[merged.length - 1];
          if (r.start.getTime() <= last.end.getTime()) {
            // overlap or contiguous: extend end if needed
            if (r.end.getTime() > last.end.getTime()) {
              last.end = new Date(r.end);
            }
          } else {
            merged.push({ ...r });
          }
        }
      }

      // Sum months across merged intervals only
      let totalMonths = 0;
      for (const m of merged) {
        totalMonths += diffInMonths(m.start, m.end);
      }
      const years = Math.floor(totalMonths / 12);
      const months = totalMonths % 12;
      return { years, months, totalMonths };
    } catch (e) {
      return null;
    }
  }, [currentMember]);

  const totalServiceText = useMemo(() => {
    if (!totalService) return null;
    const parts: string[] = [];
    if (totalService.years) parts.push(`${totalService.years} year${totalService.years === 1 ? '' : 's'}`);
    if (totalService.months) parts.push(`${totalService.months} month${totalService.months === 1 ? '' : 's'}`);
    if (parts.length === 0) return 'Less than a month';
    return parts.join(' ');
  }, [totalService]);

  // Admin/interrogation state
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [interrogateJobId, setInterrogateJobId] = useState<string | null>(null);
  const [interrogateStatus, setInterrogateStatus] = useState<any | null>(null);
  const [interrogating, setInterrogating] = useState<boolean>(false);
  const [pollHandle, setPollHandle] = useState<any>(null);

  useEffect(() => {
    // reset AI summary state on member change to avoid stale display
    setAiSummary(null);
    setAiSummaryStats({});
    setAiSummaryTime(null);
    if (bioguideId) {
      dispatch(fetchMemberDetail(bioguideId));
      // fetch sponsored/cosponsored lists
      (async () => {
        try {
          const [sResp, cResp] = await Promise.all([
            apiService.getMemberSponsoredLegislation(bioguideId, { limit: 25, offset: 0 }),
            apiService.getMemberCosponsoredLegislation(bioguideId, { limit: 25, offset: 0 }),
          ]);
          setSponsored(sResp.data?.bills || []);
          setCosponsored(cResp.data?.bills || []);
        } catch (e) {
          console.error('Failed to fetch member legislation', e);
          setSponsored([]);
          setCosponsored([]);
        }
        // fetch AI summary
        try {
          setAiSummaryLoading(true);
          setAiSummaryError(null);
          const resp = await apiService.getMemberAISummary(bioguideId);
          if (resp?.data?.bioguide_id === bioguideId) {
            setAiSummary(resp.data?.summary || null);
            setAiSummaryStats(resp.data?.stats || {});
            setAiSummaryTime(resp.data?.generated_at || null);
          } else {
            setAiSummary(null);
            setAiSummaryTime(null);
          }
        } catch (e: any) {
          // Gracefully handle missing or transient errors without noisy console spam
          const status = e?.response?.status;
          if (status === 404) {
            // Summary not yet available; keep UI quiet and allow manual refresh
            setAiSummary(null);
            setAiSummaryTime(null);
            setAiSummaryError(null);
          } else {
            setAiSummary(null);
            setAiSummaryTime(null);
            setAiSummaryError('AI summary is temporarily unavailable. You can try Refresh.');
          }
        } finally {
          setAiSummaryLoading(false);
        }
      })();
    }
  }, [dispatch, bioguideId]);

  // Ensure API service uses the latest bearer token for authenticated calls
  useEffect(() => {
    apiService.setToken(token || null);
  }, [token]);

  // Determine admin privileges from Keycloak token
  useEffect(() => {
    try {
      if (!token) { setIsAdmin(false); return; }
      const parts = token.split('.');
      if (parts.length < 2) { setIsAdmin(false); return; }
      const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadJson);
      const roles = (payload?.realm_access?.roles || []).map((r: any) => String(r).toLowerCase());
      const groups = (payload?.groups || []).map((g: any) => String(g).toLowerCase().replace(/^\//, ''));
      const adminMarkers = new Set(['administrator', 'admin', 'admins']);
      const isAdm = roles.some((r: string) => adminMarkers.has(r)) || groups.some((g: string) => adminMarkers.has(g));
      setIsAdmin(isAdm);
    } catch (e) {
      setIsAdmin(false);
    }
  }, [token]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollHandle) clearInterval(pollHandle);
    };
  }, [pollHandle]);

  const startInterrogation = async () => {
    if (!bioguideId) return;
    try {
      setInterrogating(true);
      setInterrogateStatus({ state: 'queued', percent: 0, message: 'Queued' });
      const resp = await apiService.startMemberInterrogation(bioguideId);
      const jid = resp?.data?.job_id;
      setInterrogateJobId(jid);
      // begin polling
      const handle = setInterval(async () => {
        try {
          const s = await apiService.getMemberInterrogationStatus(bioguideId, jid);
          const st = s?.data || null;
          setInterrogateStatus(st);
          if (!st || st.state === 'completed' || st.state === 'failed') {
            clearInterval(handle);
            setPollHandle(null);
            setInterrogating(false);
          }
        } catch (e) {
          console.error('Polling interrogation status failed', e);
          clearInterval(handle);
          setPollHandle(null);
          setInterrogating(false);
        }
      }, 1500);
      setPollHandle(handle);
    } catch (e) {
      console.error('Failed to start interrogation', e);
      setInterrogating(false);
      setInterrogateStatus({ state: 'failed', percent: 0, message: 'Failed to start' });
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading member details..." />;
  }

  if (!currentMember) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/members')}>
          Back to Members
        </Button>
        <Typography variant="h6" sx={{ mt: 2 }}>
          Member not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Sticky Top Bar */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1100, bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(6px)', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
          {(() => {
            const imgUrl = (currentMember as any).imageUrl || (currentMember as any).image_url;
            return imgUrl ? (
              <Box component="img" src={imgUrl} alt={(currentMember as any).name} sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }} />
            ) : null;
          })()}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>{(currentMember as any).name}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[ (currentMember as any).party, (currentMember as any).state, (currentMember as any).chamber ].filter(Boolean).join(' • ')}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant={isFollowing ? 'outlined' : 'contained'}
              onClick={async () => {
                if (!authenticated) { login(); return; }
                try {
                  if (isFollowing) {
                    await apiService.unfollowMemberByBioguide(String(bioguideId));
                    setIsFollowing(false);
                  } else {
                    await apiService.followMemberByBioguide(String(bioguideId));
                    setIsFollowing(true);
                  }
                } catch {}
              }}
            >
              {isFollowing ? 'Following' : `Follow ${(currentMember as any).lastName || ''}`.trim() || 'Follow'}
            </Button>
            <Button size="small" variant="outlined" onClick={() => navigate('/me', { state: { tab: 'follows' } })}>Get updates</Button>
            <Button size="small" variant="outlined" onClick={() => navigate('/chat', { state: { memberContext: [String(bioguideId)] } })}>Ask about this member</Button>
          </Box>
        </Box>
      </Box>

      <Button startIcon={<ArrowBack />} onClick={() => navigate('/members')} sx={{ mb: 2, mt: 1 }}>
        Back to Members
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={3}>
          {(() => {
            const imgUrl = (currentMember as any).imageUrl || currentMember.image_url;
            const imgAttr = (currentMember as any).imageAttribution || (currentMember as any).image_attribution;
            if (!imgUrl) return null;
            return (
              <Box>
                <Box component="img" src={imgUrl} alt={currentMember.name} sx={{ width: 96, height: 96, borderRadius: '8px', objectFit: 'cover', boxShadow: 1, display: 'block' }} />
                {imgAttr && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block', maxWidth: 96 }}>
                    {imgAttr}
                  </Typography>
                )}
              </Box>
            );
          })()}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ m: 0 }}>
              {currentMember.name}
            </Typography>
            {(() => {
              try {
                const mState = (currentMember.state || '').toUpperCase();
                const mDist = (currentMember.district || '') as string;
                const locState = ((myLocation?.state || '') as string).toUpperCase();
                const locDist = (myLocation?.district || '') as string;
                const isSen = locState && mState === locState && (!mDist || mDist === '' || mDist === '0');
                const isRep = locState && mState === locState && locDist && String(mDist) === String(locDist);
                return (
                  <>
                    {isRep && <Chip color="success" label="Your Representative" size="small" />}
                    {isSen && <Chip color="info" label="Your Senator" size="small" />}
                  </>
                );
              } catch {
                return null;
              }
            })()}
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Button
              variant={isFollowing ? 'outlined' : 'contained'}
              onClick={async () => {
                if (!authenticated) { login(); return; }
                try {
                  if (isFollowing) {
                    await apiService.unfollowMemberByBioguide(String(bioguideId));
                    setIsFollowing(false);
                  } else {
                    await apiService.followMemberByBioguide(String(bioguideId));
                    setIsFollowing(true);
                  }
                } catch (e) { /* no-op */ }
              }}
            >
              {isFollowing ? 'Unfollow' : 'Follow'}
            </Button>
          </Box>
        </Box>

        {isAdmin && (
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Button variant="contained" color="secondary" disabled={interrogating} onClick={startInterrogation}>
              {interrogating ? 'Interrogating…' : 'Interrogate'}
            </Button>
            {interrogateStatus && (
              <Box flex={1}>
                <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, Number(interrogateStatus.percent || 0)))} />
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {interrogateStatus.message} {typeof interrogateStatus.processed === 'number' && typeof interrogateStatus.total === 'number' ? `(${interrogateStatus.processed}/${interrogateStatus.total})` : ''}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Notes */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>My Notes</Typography>
          {noteSaved && <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>{noteSaved}</Typography>}
          {noteError && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{noteError}</Typography>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <TextField
              label="Add a note to this member"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={async () => {
                if (!authenticated) { login(); return; }
                setNoteError(null); setNoteSaved(null);
                try {
                  await apiService.createNoteForTarget({ target_type: 'member', target_ref: String(bioguideId), text: noteText.trim() });
                  setNoteSaved('Saved');
                  setNoteText('');
                } catch (e: any) {
                  setNoteError(e?.response?.data?.detail || 'Failed to save note');
                }
              }}
              disabled={!noteText.trim()}
            >
              Save Note
            </Button>
          </Stack>
        </Paper>

        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          {currentMember.state && (
            <Chip label={currentMember.state} />
          )}
          {currentMember.party && (
            <Chip 
              label={currentMember.party} 
              variant="outlined"
              color={
                currentMember.party === 'Democratic' ? 'primary' :
                currentMember.party === 'Republican' ? 'secondary' : 'default'
              }
            />
          )}
          {currentMember.chamber && (
            <Chip label={currentMember.chamber} color="secondary" />
          )}
        </Box>
      </Paper>

      {/* Right rail intro row: leave left empty on desktop to avoid big refactor */}
      {/* Intro row: recent social posts left; key facts and ask widget right */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          {/* Recent Social Posts */}
          {bioguideId && (
            <MemberSocialWidget bioguideId={bioguideId} />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {/* Key Facts Card */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Key Facts</Typography>
              {(() => {
                const party = (currentMember as any)?.party as string | undefined;
                const state = (currentMember as any)?.state as string | undefined;
                const chamber = (currentMember as any)?.chamber as string | undefined;
                let district = (currentMember as any)?.district as string | number | undefined;
                // Friendly fallback for non‑House seats or at‑large
                const isSenate = (chamber || '').toLowerCase() === 'senate';
                if (!district || String(district) === '0' || isSenate) {
                  district = isSenate ? 'Statewide' : (district ? String(district) : 'At‑Large');
                }
                return (
                  <Box>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>Party</Typography>
                          <Chip
                            size="small"
                            label={party || 'N/A'}
                            color={party === 'Democratic' ? 'primary' : (party === 'Republican' ? 'secondary' : 'default')}
                            variant={party ? 'filled' : 'outlined'}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>State</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{state || 'N/A'}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>District</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{String(district || 'N/A')}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })()}
            </CardContent>
          </Card>

          {/* Mini AI Q&A */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Ask about this member</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Type a question…"
                value={miniQuestion}
                onChange={(e) => setMiniQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && miniQuestion.trim()) {
                    navigate('/chat', { state: { memberContext: [String(bioguideId)], initialQuestion: miniQuestion.trim() } });
                  }
                }}
                sx={{ mb: 1 }}
              />
              <Button fullWidth variant="contained" disabled={!miniQuestion.trim()} onClick={() => navigate('/chat', { state: { memberContext: [String(bioguideId)], initialQuestion: miniQuestion.trim() } })}>
                Ask
              </Button>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[
                  'What bills define their agenda?',
                  'How often do they vote with their party?',
                  'How do they stand on HR ____?'
                ].map((s, i) => (
                  <Chip key={i} size="small" label={s} onClick={() => navigate('/chat', { state: { memberContext: [String(bioguideId)], initialQuestion: s } })} />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {bioguideId && (
          <>
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <SponsorNetworkGraph bioguideId={bioguideId} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card>
                <CardContent>
                  <SimilarMembersList bioguideId={bioguideId} />
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Finance */}
        {bioguideId && (
          <Grid item xs={12}>
            <FinanceTab bioguideId={bioguideId} isAdmin={isAdmin} />
          </Grid>
        )}

        {/* AI Summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" flexWrap="wrap" gap={1} justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" component="h2">AI Summary of Legislative Focus</Typography>
                <Box textAlign="right">
                  <Button
                    size="small"
                    disabled={aiSummaryLoading}
                    onClick={async () => {
                      if (!bioguideId) return;
                      try {
                        setAiSummaryLoading(true);
                        setAiSummaryError(null);
                        const resp = await apiService.getMemberAISummary(bioguideId);
                        setAiSummary(resp.data?.summary || null);
                        setAiSummaryStats(resp.data?.stats || {});
                        setAiSummaryTime(resp.data?.generated_at || null);
                      } catch (e) {
                        setAiSummaryError('Unable to reload the saved summary. Please try again.');
                      } finally {
                        setAiSummaryLoading(false);
                      }
                    }}
                  >
                    {aiSummaryLoading ? 'Loading...' : 'Reload Summary'}
                  </Button>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Summaries update in the background. Reload checks for the latest saved summary.
                  </Typography>
                </Box>
              </Box>
              {aiSummaryError && aiSummary && <Typography color="error">{aiSummaryError}</Typography>}
              {aiSummaryLoading && !aiSummary ? (
                <Typography variant="body2" color="textSecondary">Loading saved summary...</Typography>
              ) : aiSummary ? (
                <>
                  {/* Render trusted counts separately from AI prose. */}
                  <MemberSummary text={aiSummary} stats={aiSummaryStats} />
                  {aiSummaryTime && (
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                      Last updated: {new Date(aiSummaryTime).toLocaleString()}
                    </Typography>
                  )}
                </>
              ) : aiSummaryError ? (
                <Typography variant="body2" color="textSecondary">{aiSummaryError}</Typography>
              ) : (
                <Typography variant="body2" color="textSecondary">No AI summary is available yet. Summaries update in the background.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Bioguide ID:</strong> {currentMember.bioguideId}
              </Typography>
              {currentMember.district && (
                <Typography variant="body1" paragraph>
                  <strong>District:</strong> {currentMember.district}
                </Typography>
              )}
              {currentMember.partyHistory && (
                <Typography variant="body1" paragraph>
                  <strong>Party History:</strong> {currentMember.partyHistory}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Service Information
              </Typography>
              {totalServiceText && (
                <Typography variant="body1" paragraph>
                  <strong>Total Time Served:</strong> {totalServiceText}
                </Typography>
              )}
              {currentMember.terms && currentMember.terms.length > 0 && (
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Current/Recent Terms:
                  </Typography>
                  {currentMember.terms.map((term: any, index: number) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        {term.chamber} - {(term.startYear || term.startDate || term.start || term.start_year || term.start_date)} to {(term.endYear || term.endDate || term.end || term.end_year || term.end_date || 'Present')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {currentMember.leadership && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Leadership Roles
                </Typography>
                {currentMember.leadership.map((role: any, index: number) => (
                  <Box key={index} sx={{ mb: 1 }}>
                    <Typography variant="body1">
                      <strong>{role.title}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {role.congress ? `Congress ${role.congress}` : ''}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recent Votes */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Votes</Typography>
              {memberVotes === null ? (
                <Typography variant="body2" color="textSecondary">Loading…</Typography>
              ) : memberVotes.length === 0 ? (
                <Typography variant="body2" color="textSecondary">No recent votes found.</Typography>
              ) : (
                <List>
                  {memberVotes.map((v: any, idx: number) => (
                    <ListItem key={idx} divider>
                      <ListItemText
                        primary={`${v.question || 'Vote'} — ${v.result || ''}`}
                        secondary={`${v.date ? new Date(v.date).toLocaleString() : ''}${v.position ? ` • Member position: ${v.position}` : ''}`}
                      />
                      {v.legislationType && v.legislationNumber && (
                        <Button size="small" onClick={() => navigate(`/bills/${v.congress}/${String(v.legislationType || '').toLowerCase()}/${v.legislationNumber}`)}>View Bill</Button>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sponsored legislation */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Sponsored Legislation</Typography>
                <Button size="small" onClick={() => bioguideId && navigate(`/members/${bioguideId}`)}>Refresh</Button>
              </Box>
              {sponsored === null ? (
                <Typography variant="body2" color="textSecondary">Loading...</Typography>
              ) : sponsored.length === 0 ? (
                <Typography variant="body2" color="textSecondary">No sponsored bills found.</Typography>
              ) : (
                <List>
                  {sponsored.map((b: any, idx: number) => (
                    <ListItem key={idx} divider sx={{ pr: 20, alignItems: 'flex-start' }} secondaryAction={
                      <Box display="flex" gap={1}>
                        <Button size="small" onClick={() => navigate(`/bills/${b.congress}/${b.bill_type}/${b.bill_number}`)}>View</Button>
                        <Button size="small" variant="outlined" onClick={() => navigate('/chat', { state: { billContext: [`${b.congress}:${b.bill_type}:${b.bill_number}`] } })}>Chat</Button>
                      </Box>
                    }>
                      <ListItemText
                        primary={`${(b.bill_type||'').toUpperCase()} ${b.bill_number}${b.title ? `: ${b.title}` : ''}`}
                        secondary={b.latest_action_text || ''}
                        primaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                        secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                        sx={{ mr: 1 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cosponsored legislation */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Cosponsored Legislation</Typography>
                <Button size="small" onClick={() => bioguideId && navigate(`/members/${bioguideId}`)}>Refresh</Button>
              </Box>
              {cosponsored === null ? (
                <Typography variant="body2" color="textSecondary">Loading...</Typography>
              ) : cosponsored.length === 0 ? (
                <Typography variant="body2" color="textSecondary">No cosponsored bills found.</Typography>
              ) : (
                <List>
                  {cosponsored.map((b: any, idx: number) => (
                    <ListItem key={idx} divider sx={{ pr: 20, alignItems: 'flex-start' }} secondaryAction={
                      <Box display="flex" gap={1}>
                        <Button size="small" onClick={() => navigate(`/bills/${b.congress}/${b.bill_type}/${b.bill_number}`)}>View</Button>
                        <Button size="small" variant="outlined" onClick={() => navigate('/chat', { state: { billContext: [`${b.congress}:${b.bill_type}:${b.bill_number}`] } })}>Chat</Button>
                      </Box>
                    }>
                      <ListItemText
                        primary={`${(b.bill_type||'').toUpperCase()} ${b.bill_number}${b.title ? `: ${b.title}` : ''}`}
                        secondary={b.latest_action_text || ''}
                        primaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                        secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                        sx={{ mr: 1 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
};

export default MemberDetail;
